import React from "react";
import {
  SimpleGrid,
  Card,
  CardBody,
  HStack,
  Badge,
  VStack,
  Text,
  Box,
  Icon,
  useBreakpointValue,
  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import {
  FiDollarSign,
  FiTarget,
  FiUser,
  FiActivity,
  FiTrendingUp,
  FiTrendingDown,
  FiAlertTriangle,
  FiCheckCircle,
  FiCheckCircle as FiBarChart3,
  FiLock,
} from "react-icons/fi";
import { useProjectPermission } from "../../hooks/useProjectPermission"; // ✅ Import permission hook

const BudgetSummary = ({ summary }) => {
  // ✅ Get user's project role and permissions
  const { isAdmin, isProjectManager, canViewBudget } = useProjectPermission();

  // ✅ Check if user has permission to view budget
  const hasAccess = isAdmin || isProjectManager || canViewBudget;

  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 2,
    }).format(num || 0);
  };

  const formatCurrency = (amount, currency) => {
    const projectCurrency = currency || summary.currency || "AED";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: projectCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDetailedCurrency = (amount, currency) => {
    const projectCurrency = currency || summary.currency || "AED";

    if (amount >= 1000000) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: projectCurrency,
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
        notation: "compact",
        compactDisplay: "short",
      }).format(amount);
    }
    return formatCurrency(amount, projectCurrency);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return "green";
    if (percentage >= 60) return "yellow";
    return "red";
  };

  const getCPIColor = (cpi) => {
    if (cpi < 0.85) return "red";
    if (cpi < 0.90) return "orange";
    if (cpi < 0.95) return "yellow";
    return "green";
  };

  // Cost performance calculations
  const earnedValue =
    (summary.startedTotalConsumedCost || 0) *
    ((summary.progressPercentage || 0) / 100);
  const actualCost = summary.totalConsumedCost || 0;
  const costPerformanceIndex = summary.startedProjectCostPerformanceIndex ?? 0;
  const costVariance = summary.costVariance || 0;
  const costVariancePercentage =
    summary.startedTotalBudgetedCost > 0
      ? (costVariance / summary.startedTotalConsumedCost) * 100
      : 0;

  const quantityProgress =
    summary.totalBudgeted > 0
      ? summary.totalEarnedManhours / summary.totalBudgeted
      : 0;

  const taskInstallationProgress =
    summary.totalTasks > 0
      ? (summary.startedTotalTask / summary.totalTasks) * 100
      : 0;

  const remainingTasks =
    (summary.totalTasks || 0) - (summary.completedTasks || 0);

  const columns = useBreakpointValue({ base: 1, md: 2, lg: 3, xl: 6 });

  // ✅ If user doesn't have access, show restricted message
  if (!hasAccess) {
    return (
      <Text>
      
      </Text>
    );
  }
   
  // ✅ If user has access, show the full budget summary
  return (
    <SimpleGrid columns={columns} spacing={6} w="100%">
      {/* Total Project Budget Card */}
      <Card
        bg="gray.800"
        borderWidth="1px"
        borderColor="purple.500"
        boxShadow="lg"
        _hover={{ boxShadow: "xl", transform: "translateY(-2px)" }}
        transition="all 0.2s"
      >
        <CardBody p={5}>
          <VStack spacing={4} align="stretch">
            <HStack spacing={3}>
              <Box p={2} bg="purple.500" borderRadius="md">
                <Icon as={FiUser} color="white" boxSize={4} />
              </Box>
              <VStack spacing={0} align="start" flex={1}>
                <Text fontSize="xs" color="gray.400" fontWeight="medium">
                  Total Manhours
                </Text>
              </VStack>
            </HStack>

            <VStack spacing={1} align="start">
              <Text fontSize="2xl" fontWeight="bold" color="white">
                {formatNumber(summary.totalBudgeted || 0) || 0} MH
              </Text>
              <Text fontSize="sm" color="gray.400">
                {formatNumber(summary.totalEarnedManhours || 0)} manhours earned{" "}
                <br />
                {formatNumber(summary.startedTotalConsumedManhours || 0)}{" "}
                manhours used <br />
              </Text>
            </VStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Budgeted Cost Card */}
      <Card
        bg="gray.800"
        borderWidth="1px"
        borderColor="green.500"
        boxShadow="lg"
        _hover={{ boxShadow: "xl", transform: "translateY(-2px)" }}
        transition="all 0.2s"
      >
        <CardBody p={5}>
          <VStack spacing={4} align="stretch">
            <HStack spacing={3}>
              <Box p={2} bg="green.500" borderRadius="md">
                <Icon as={FiDollarSign} color="white" boxSize={4} />
              </Box>
              <VStack spacing={0} align="start" flex={1}>
                <Text fontSize="xs" color="gray.400" fontWeight="medium">
                  Budgeted Cost
                </Text>
              </VStack>
            </HStack>

            <VStack spacing={1} align="start">
              <Text fontSize="2xl" fontWeight="bold" color="white">
                {formatDetailedCurrency(summary.totalBudgetedCost || 0)}
              </Text>
              <Text fontSize="sm" color="gray.400">
                Total planned budget
              </Text>
            </VStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Cost Performance Index Card */}
      <Card
        bg="gray.800"
        borderWidth="1px"
        borderColor={
          costPerformanceIndex >= 0.95
            ? "green.500"
            : costPerformanceIndex >= 0.9
            ? "yellow.500"
            : costPerformanceIndex >= 0.85
            ? "orange.500"
            : "red.500"
        }
        boxShadow="lg"
        _hover={{ boxShadow: "xl", transform: "translateY(-2px)" }}
        transition="all 0.2s"
      >
        <CardBody p={5}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <HStack spacing={3}>
                <Box
                  p={2}
                  bg={
                    costPerformanceIndex >= 0.95
                      ? "green.500"
                      : costPerformanceIndex >= 0.9
                      ? "yellow.500"
                      : costPerformanceIndex >= 0.85
                      ? "orange.500"
                      : "red.500"
                  }
                  borderRadius="md"
                >
                  <Icon as={FiBarChart3} color="white" boxSize={4} />
                </Box>
                <Text fontSize="xs" color="gray.400" fontWeight="medium">
                  CPI
                </Text>
              </HStack>
              {/* Hide badge when CPI is 0 (project not started) */}
              {costPerformanceIndex > 0 && (
                <Badge
                  colorScheme={getCPIColor(costPerformanceIndex)}
                  variant="solid"
                  fontSize="xs"
                  px={2}
                  py={1}
                  borderRadius="full"
                >
                  {costPerformanceIndex >= 0.95
                    ? "GOOD"
                    : costPerformanceIndex >= 0.9
                    ? "MEDIUM"
                    : costPerformanceIndex >= 0.85
                    ? "HIGH"
                    : "V HIGH"}
                </Badge>
              )}
            </HStack>

            <VStack spacing={1} align="start">
              <Text fontSize="2xl" fontWeight="bold" color="white">
                {costPerformanceIndex.toFixed(2) <= 1
                  ? costPerformanceIndex.toFixed(2)
                  : "1.00"}
              </Text>
              <Text fontSize="xs" color="gray.500">
                EV: {formatNumber(summary.totalEarnedManhours)} <br />
                Actual MH: {formatNumber(summary.startedTotalConsumedManhours)}{" "}
                <br />
              </Text>
            </VStack>

            <Progress
              value={Math.min(costPerformanceIndex * 100, 150)}
              colorScheme={getCPIColor(costPerformanceIndex)}
              size="sm"
              bg="gray.700"
              borderRadius="full"
            />
          </VStack>
        </CardBody>
      </Card>

      {/* Cost Variance Card */}
      <Card
        bg="gray.800"
        borderWidth="1px"
        borderColor={costVariance >= 0 ? "green.500" : "red.500"}
        boxShadow="lg"
        _hover={{ boxShadow: "xl", transform: "translateY(-2px)" }}
        transition="all 0.2s"
      >
        <CardBody p={5}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <HStack spacing={3}>
                <Box
                  p={2}
                  bg={costVariance >= 0 ? "green.500" : "red.500"}
                  borderRadius="md"
                >
                  <Icon
                    as={costVariance >= 0 ? FiTrendingUp : FiTrendingDown}
                    color="white"
                    boxSize={4}
                  />
                </Box>
                <Text fontSize="xs" color="gray.400" fontWeight="medium">
                  Cost variance
                </Text>
              </HStack>
            </HStack>

            <VStack spacing={1} align="start">
              <Text
                fontSize="2xl"
                fontWeight="bold"
                color={costVariance >= 0 ? "green.400" : "red.400"}
              >
                {summary.currency || "AED"}{" "}
                {formatDetailedCurrency(Math.abs(costVariance)).replace(
                  /[^0-9.,]/g,
                  ""
                )}
              </Text>
              <Text fontSize="sm" color="gray.400">
                lapsed MH: {Math.abs(summary.lapsedManhours)} MH
              </Text>
            </VStack>

            <HStack spacing={2}>
              <Icon
                as={costVariance >= 0 ? FiCheckCircle : FiAlertTriangle}
                color={costVariance >= 0 ? "green.400" : "red.400"}
                boxSize={4}
              />
              <Text fontSize="xs" color="gray.500">
                {costVariance < 0 ? "Significant overrun" : "Under budget"}
              </Text>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Quantity Progress Card */}
      <Card
        bg="gray.800"
        borderWidth="1px"
        borderColor="blue.500"
        boxShadow="lg"
        _hover={{ boxShadow: "xl", transform: "translateY(-2px)" }}
        transition="all 0.2s"
      >
        <CardBody p={5}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <HStack spacing={3}>
                <Box p={2} bg="blue.500" borderRadius="md">
                  <Icon as={FiTarget} color="white" boxSize={4} />
                </Box>
                <Text fontSize="xs" color="gray.400" fontWeight="medium">
                  Progress
                </Text>
              </HStack>
            </HStack>

            <VStack spacing={1} align="start">
              <Text fontSize="2xl" fontWeight="bold" color="white">
                {(quantityProgress.toFixed(2) * 100).toFixed(1)}%
              </Text>
              <Text fontSize="sm" color="gray.400">
                {formatNumber(summary.totalEarnedManhours || 0)} /{" "}
                {formatNumber(summary.totalBudgeted || 0)} MH earned
                <br />
                Total quantity : {formatNumber(summary.totalQuantity)}
                <br />
                installed quantity :{" "}
                {formatNumber(summary.startedTotalInstalledQuantity)}
              </Text>
            </VStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Task Installation Card */}
      <Card
        bg="gray.800"
        borderWidth="1px"
        borderColor="purple.500"
        boxShadow="lg"
        _hover={{ boxShadow: "xl", transform: "translateY(-2px)" }}
        transition="all 0.2s"
      >
        <CardBody p={5}>
          <VStack spacing={4} align="stretch">
            <HStack spacing={3}>
              <Box p={2} bg="purple.500" borderRadius="md">
                <Icon as={FiActivity} color="white" boxSize={4} />
              </Box>
              <Text fontSize="xs" color="gray.400" fontWeight="medium">
                Task Completion
              </Text>
            </HStack>

            <VStack spacing={1} align="start">
              <Text fontSize="2xl" fontWeight="bold" color="white">
                {summary.completedTasks || 0} / {summary.totalTasks || 0}
              </Text>
              <Text fontSize="sm" color="gray.400">
                {taskInstallationProgress.toFixed(1)}% Complete
              </Text>
            </VStack>

            <VStack spacing={2} align="stretch">
              <HStack spacing={2}>
                <Box w={2} h={2} bg="green.400" borderRadius="full" />
                <Text fontSize="xs" color="gray.400">
                  {summary.completedTasks || 0} COMPLETED
                </Text>
              </HStack>
              <HStack spacing={2}>
                <Box w={2} h={2} bg="gray.500" borderRadius="full" />
                <Text fontSize="xs" color="gray.400">
                  {remainingTasks} REMAINING
                </Text>
              </HStack>
            </VStack>
          </VStack>
        </CardBody>
      </Card>
    </SimpleGrid>
  );
};

export default BudgetSummary;
